import { BreakpointObserver } from '@angular/cdk/layout';
import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  computed,
  effect,
  inject,
  input,
  signal,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { ActivatedRoute, NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';

import { filter } from 'rxjs/operators';

import { AppSwitcherService } from '../../../core/services/app-switcher.service';
import { AuthService } from '../../../core/services/auth.service';
import { CurrentUserService } from '../../../core/services/current-user.service';
import { PortalThemeService } from '../../../core/services/portal-theme.service';
import { StoreContextService } from '../../../core/services/store-context.service';
import { UserRolePermissionService } from '../../../core/services/user-role-permission.service';
import { AppSwitcherComponent } from '../app-switcher/app-switcher.component';
import { BottomNavComponent, BottomNavTab } from '../bottom-nav/bottom-nav.component';
import { CommandPaletteComponent } from '../command-palette/command-palette.component';
import { IconComponent, IconName } from '../icon/icon.component';
import { ToastHostComponent } from '../toast-host/toast-host.component';

export interface NavItem {
  label: string;
  icon: IconName;
  route: string;
  badge?: string | number;
  /** Permission required to show this item, e.g. 'Consignor:View'. Omit to always show. */
  permission?: string;
}

export interface NavSection {
  title: string;
  items: NavItem[];
}

const DESKTOP_BP = '(min-width: 1024px)';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    RouterLink,
    RouterLinkActive,
    ReactiveFormsModule,
    IconComponent,
    MatSidenavModule,
    MatToolbarModule,
    MatListModule,
    MatIconModule,
    MatButtonModule,
    MatDividerModule,
    MatFormFieldModule,
    MatSelectModule,
    BottomNavComponent,
    CommandPaletteComponent,
    ToastHostComponent,
    AppSwitcherComponent
  ],
  templateUrl: './app-layout.component.html',
  styleUrl: './app-layout.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:click)': 'closeProfile()',
    '(document:keydown.escape)': 'closeProfile()'
  }
})
export class AppLayoutComponent implements OnInit {
  readonly sidenav = viewChild<MatSidenav>('sidenav');
  private readonly profileBtn = viewChild<ElementRef<HTMLButtonElement>>('profileBtn');

  sections = input<NavSection[]>([]);
  moduleLabel = input('');

  /**
   * Mobile bottom nav. Four primary destinations + a "More" overflow that opens
   * the sidenav drawer for everything else. Sub-1024px only.
   */
  readonly bottomNavTabs: BottomNavTab[] = [
    { label: 'Dashboard', icon: 'home', route: '/dashboard' },
    { label: 'Consignors', icon: 'users', route: '/consignors' },
    { label: 'Items', icon: 'package', route: '/items' },
    { label: 'Payouts', icon: 'dollar', route: '/payouts' },
    { label: 'More', icon: 'more-vertical', action: 'more' }
  ];

  profileOpen = signal(false);

  closeProfile(): void {
    if (!this.profileOpen()) return;
    this.profileOpen.set(false);
    // Restore focus to the trigger so keyboard users don't lose their place.
    this.profileBtn()?.nativeElement.focus();
  }

  protected onBottomNavAction(action: string): void {
    if (action === 'more') {
      this.sidenav()?.open();
    }
  }

  protected isNavItemVisible(item: NavItem): boolean {
    return !item.permission || this.userRolePermission.hasPermission(item.permission);
  }

  protected readonly currentUser = inject(CurrentUserService);
  protected readonly storeCtx = inject(StoreContextService);
  protected readonly appSwitcher = inject(AppSwitcherService);
  protected readonly userRolePermission = inject(UserRolePermissionService);
  private readonly authService = inject(AuthService);
  private readonly bpObserver = inject(BreakpointObserver);
  private readonly document = inject(DOCUMENT);
  activeCrumb = signal('');
  isDesktop = signal(false);

  /** Active-store picker — accepting stores only; selection feeds StoreContextService. */
  protected readonly storeControl = new FormControl<number | null>(null);

  constructor() {
    // Reflect the active store (initial resolution or any other source) into the control.
    effect(() => {
      const id = this.storeCtx.selectedStoreId();
      if (this.storeControl.value !== id) {
        this.storeControl.setValue(id, { emitEvent: false });
      }
    });

    // User picked a store → persist it, then reload so every page re-fetches
    // under the new store. The selection is persisted, so init restores it.
    this.storeControl.valueChanges.pipe(takeUntilDestroyed()).subscribe(id => {
      if (id == null || id === this.storeCtx.selectedStoreId()) return;
      this.storeCtx.selectStore(id);
      this.document.defaultView?.location.reload();
    });
  }

  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly portalTheme = inject(PortalThemeService);

  protected profileSubtitle = computed(() => this.currentUser.email());

  /** Consignment-app roles only (API-scoped via `?Apps=`) — displayed as chips in the toolbar profile button. */
  protected readonly ddRoles = computed<string[]>(() => this.userRolePermission.roles().map(r => r.name));

  ngOnInit() {
    this.activeCrumb.set(this.getRouteTitle());

    this.router.events
      .pipe(
        filter(e => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.activeCrumb.set(this.getRouteTitle());
        this.profileOpen.set(false);
        if (!this.isDesktop() && this.sidenav()) {
          this.sidenav()!.close();
        }
        const main = this.document.getElementById('main-content');
        if (main) {
          main.focus();
        }
      });

    this.bpObserver
      .observe(DESKTOP_BP)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        this.isDesktop.set(result.matches);
      });
  }

  signOut(): void {
    this.authService.broadcastLogout();
    this.currentUser.clear();
    this.storeCtx.reset();
    this.authService.performLogout();
  }

  private getRouteTitle(): string {
    let r = this.route.root;
    let label = '';
    while (r.firstChild) {
      r = r.firstChild;
      if (r.snapshot.data['breadcrumb']) label = r.snapshot.data['breadcrumb'];
    }
    return label || this.moduleLabel();
  }
}
